import { useEffect, useMemo } from 'react';
import { UserKind } from 'src/api/generated';
import type { SemanticQuickAutomationKind } from 'src/modules/automations/types/semantic-quick-automation.type';
import { useAllUsers } from 'src/modules/shared/hooks/api/admin/users';
import { useFunnelStatuses } from 'src/modules/shared/hooks/api/crm/funnels/funnel-statuses';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { UserPermission } from 'src/modules/shared/types/permissions.type';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';

const MAX_STATUS_PAGE_SIZE = 500;

/**
 * Loads tenant reference data used by exact semantic quick-automation adapters.
 *
 * Statuses are scoped to the current Communications funnel. Person Users are
 * loaded only for adapters that require an assignee and only after the
 * canonical User read permission has been confirmed.
 *
 * @param funnelId - Funnel currently displayed in Communications.
 * @param kind - Exact semantic adapter selected for the current template.
 * @param enabled - Whether the selected template is safe to configure.
 * @returns Permission-aware statuses, person Users, loading, and retry state.
 */
export const useSemanticQuickAutomationSetup = (
  funnelId: number | undefined,
  kind: SemanticQuickAutomationKind | null,
  enabled: boolean
) => {
  const statusesEnabled = enabled && typeof funnelId === 'number';
  const needsUsers = enabled && kind === 'status-and-person-user';
  const statusQuery = useMemo(
    () =>
      createQuery()
        .equals('funnelId', funnelId ?? 0)
        .orderByAsc('position')
        .take(MAX_STATUS_PAGE_SIZE)
        .build(),
    [funnelId]
  );
  const personUserFilters = useMemo(
    () => ({
      filterBy: createQuery().equals('kind', UserKind.PERSON).build().filterBy,
    }),
    []
  );
  const { hasPermission: canReadUsers, loading: usersPermissionLoading } =
    useHasPermissions(UserPermission.Read);
  const usersEnabled = needsUsers && canReadUsers && !usersPermissionLoading;

  const {
    statuses,
    loading: statusesLoading,
    error: statusesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchStatuses,
  } = useFunnelStatuses(statusQuery, statusesEnabled);
  const {
    users,
    loading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useAllUsers(personUserFilters, usersEnabled);

  useEffect(() => {
    if (
      statusesEnabled &&
      !statusesError &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    statusesEnabled,
    statusesError,
  ]);

  const personUsers = useMemo(
    () => users.filter((user) => user.kind === UserKind.PERSON),
    [users]
  );
  const loading =
    enabled &&
    (statusesLoading ||
      hasNextPage ||
      isFetchingNextPage ||
      (needsUsers &&
        (usersPermissionLoading || (canReadUsers && usersLoading))));
  const error =
    statusesError ?? (needsUsers && canReadUsers ? usersError : null);

  const refetch = async () => {
    await Promise.all([
      refetchStatuses(),
      ...(usersEnabled ? [refetchUsers()] : []),
    ]);
  };

  return {
    statuses,
    users: personUsers,
    canReadUsers,
    usersPermissionLoading,
    loading,
    error,
    refetch,
  };
};
